// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { deleteSubscription, getSubscriptions, subscriptionsSelector } from '../../feature';

// Components
import { Modal, ModalSize, showErrorToast, showSuccessToast } from '@/components';
import { DownloadIcon } from '@/icons';
import { Button } from '@/ui';
import { StyledContentBlock, StyledPageTitle, SubscriptionsList } from '../../components';
import { StyledActionButtons, StyledTabTitleWrapper } from './SubscriptionsContainerStyles';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { ISubscription } from '@/models/subscriptions.model';

// Helpers
import { SubscriptionsAPI } from '@/api';
import { useBoolean, usePagination } from '@/hooks';
import { SubscriptionsDTO } from '../../dtos';

const SubscriptionContainer = () => {
  const { t } = useTranslation(['subscriptions']);
  const { canManage } = usePermissions();
  const canManageSubscriptions = canManage(Resource.SUBSCRIPTION);

  const { currentPage, onPageChange } = usePagination();
  const [selectedItem, setSelectedItem] = useState<ISubscription | null>(null);
  const { value: isExportLoading, setValue: setIsExportLoading } = useBoolean(false);

  const dispatch = useAppDispatch();
  const { subscriptions, isLoading, pagination, query } = useAppState(subscriptionsSelector);

  useEffect(() => {
    const dto = new SubscriptionsDTO({});
    dispatch(getSubscriptions(dto));
  }, []);

  const handleCloseDeleteModal = () => setSelectedItem(null);

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;

    try {
      dispatch(deleteSubscription(selectedItem.id)).unwrap();
      setSelectedItem(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleExport = async () => {
    setIsExportLoading(true);
    try {
      const { data } = await SubscriptionsAPI.exportSubscriptions();
      window.open(data.data.url, '_self');
      showSuccessToast({ message: t('subscriptions_import_export_success') });
    } catch (error) {
      showErrorToast({ message: t('subscriptions_import_export_error') });
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleChangePage = (page: number) => {
    const dto = new SubscriptionsDTO({ page, ...query });
    dispatch(getSubscriptions(dto));
    onPageChange(page);
  };

  return (
    <>
      <StyledTabTitleWrapper>
        <StyledPageTitle>{t('page_title')}</StyledPageTitle>
        <Button type="primary" onClick={handleExport} loading={isExportLoading} icon={<DownloadIcon />}>
          {t('subscriptions_import_export')}
        </Button>
      </StyledTabTitleWrapper>
      <StyledContentBlock>
        <SubscriptionsList
          list={subscriptions ?? []}
          isLoading={isLoading}
          currentPage={currentPage}
          total={pagination?.total ?? 0}
          onPageChange={handleChangePage}
          onDelete={setSelectedItem}
          canManage={canManageSubscriptions}
        />
        <Modal
          visible={canManageSubscriptions && !!selectedItem}
          onCancel={handleCloseDeleteModal}
          size={ModalSize.MEDIUM}
          title={t('subscriptions_delete_modal_title')}
          withFooter
          footer={
            <StyledActionButtons>
              <Button block onClick={handleCloseDeleteModal} type="primary">
                {t('button_common_back', { ns: 'common' })}
              </Button>
              <Button block onClick={handleConfirmDelete} type="text">
                {t('button_common_delete', { ns: 'common' })}
              </Button>
            </StyledActionButtons>
          }
        />
      </StyledContentBlock>
    </>
  );
};

export default SubscriptionContainer;
